/**
 * The API client.
 *
 * One place that knows how to talk to bastiond, so error handling and the session-expiry
 * behavior are consistent everywhere rather than reimplemented per screen.
 *
 * Two conventions worth stating:
 *
 *  - `credentials: 'same-origin'` on every call. The session is an HttpOnly cookie, so there is
 *    no token for the app to hold, forward, or accidentally log.
 *  - A 401 anywhere clears the cached session and notifies listeners rather than throwing into
 *    whichever screen happened to be open. An expired session mid-shift should return the
 *    analyst to the login screen, not scatter identical error toasts across five panels.
 */

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

type UnauthorizedListener = () => void
const unauthorizedListeners = new Set<UnauthorizedListener>()

export function onUnauthorized(fn: UnauthorizedListener): () => void {
  unauthorizedListeners.add(fn)
  return () => unauthorizedListeners.delete(fn)
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    // A network failure is the one error worth phrasing as a suggestion: nine times out of ten
    // in development it means the control plane is not running.
    throw new ApiError(0, 'Could not reach the Bastion control plane. Is bastiond running?')
  }

  if (resp.status === 401) {
    unauthorizedListeners.forEach((fn) => fn())
    throw new ApiError(401, 'Your session has expired. Sign in again.')
  }

  const text = await resp.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!resp.ok) {
    const message =
      (payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : null) ?? `${resp.status} ${resp.statusText}`
    throw new ApiError(resp.status, message)
  }
  return payload as T
}

export const api = {
  get: <T,>(path: string) => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T,>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T,>(path: string) => request<T>('DELETE', path),
}

/* ------------------------------------------------------------------------------------------ */
/* Types mirroring the server's shapes. Hand-written rather than generated: the surface is small
 * enough that a generator would be more machinery than it saves, and writing them by hand keeps
 * the console honest about which fields it actually depends on. */
/* ------------------------------------------------------------------------------------------ */

export type Severity = 'info' | 'low' | 'medium' | 'high' | 'critical'

export interface Me {
  id: string
  display_name: string
  tenant: string
  tenant_display_name: string
  roles: string[]
  is_machine: boolean
  capabilities: string[]
  available_tenants: { slug: string; display_name: string }[]
  env: string
  /** Whether this principal has a local password to change; false for anyone via the IdP. */
  local: boolean
  /** The local account name, where there is one. Null for a federated principal. */
  username: string | null
}

export interface Counters {
  events_ingested: number
  events_sampled_away: number
  signals_raised: number
  incidents_opened: number
  playbook_runs_started: number
  actions_performed: number
  actions_failed: number
  intel_matches: number
}

export interface Overview {
  tenant: string
  counters: Counters
  open_incidents: number
  incidents_by_severity: Record<string, number>
  events_retained: number
  signals_retained: number
  sources_configured: number
  sources_enabled: number
  rules_enabled: number
  playbooks_enabled: number
  indicators: number
  indicators_expiring_7d: number
  pir_coverage_pct: number
  coverage_gaps: number
  runs_awaiting_approval: number
  detection_degradations: number
}

export interface Observable {
  kind: string
  value: string
  role?: string
}

export interface BastionEvent {
  id: string
  tenant: string
  timestamp: number
  received_at: number
  source: string
  source_type: string
  class: string
  severity: Severity
  message: string
  fields: Record<string, unknown>
  observables: Observable[]
  raw?: string
}

export interface Technique {
  id: string
  name: string
  tactic: string
}

export interface Signal {
  id: string
  rule_id: string
  rule_version: number
  kind: string
  title: string
  severity: Severity
  timestamp: number
  evidence: string[]
  evidence_count: number
  observables: Observable[]
  techniques: Technique[]
  rationale: string
  incident?: string
}

export interface Incident {
  id: string
  tenant: string
  title: string
  status: string
  severity: Severity
  score: number
  opened_at: number
  closed_at?: number
  signals: string[]
  tactics: string[]
  channel_id: string
  assignee?: string
  playbook_run?: string
  rationale: string
}

export interface ChannelMessage {
  id: string
  channel_id: string
  author:
    | { kind: 'human'; id: string; display_name: string }
    | { kind: 'ai_analyst'; id: string; display_name: string; model: string }
    | { kind: 'system' }
  kind: string
  body: string
  at: number
  action_id?: string
  success?: boolean
  refs: string[]
  in_reply_to?: string
  reactions: Record<string, string[]>
}

export interface Channel {
  id: string
  tenant: string
  kind: string
  name: string
  topic: string
  incident_id?: string
  members: string[]
  open: boolean
  created_at: number
  archived: boolean
  messages: ChannelMessage[]
}

export interface Assessment {
  text: string
  from_model: boolean
  model: string
}

export interface IncidentDetail {
  incident: Incident
  signals: Signal[]
  events: BastionEvent[]
  channel: Channel | null
  messages: ChannelMessage[]
  runs: PlaybookRun[]
  assessment: Assessment
}

export type FieldDisposition = 'keep' | 'key_field' | 'drop' | 'hash' | 'redact'

export interface FieldRule {
  source_field: string
  rename_to?: string | null
  disposition: FieldDisposition
  observable?: string | null
  observable_role?: string
  rationale?: string
}

export interface RetentionPolicy {
  hot_days: number
  warm_days: number
  keep_raw: boolean
  raw_days: number
  keep_unmapped: boolean
  sample_one_in: number
  sampling_floor: Severity
}

export interface LogSource {
  id: string
  tenant: string
  display_name: string
  vendor: string
  format: string
  default_class: string
  default_severity: Severity
  field_rules: FieldRule[]
  retention: RetentionPolicy
  severity_field?: string | null
  severity_map: Record<string, Severity>
  class_field?: string | null
  class_map: Record<string, string>
  csv_columns: string[]
  scan_message_for_observables: boolean
  enabled: boolean
  version: number
}

export interface SourceSummary extends LogSource {
  warnings: string[]
  estimated_daily_bytes: number
}

export interface FieldProfile {
  name: string
  fill_pct: number
  distinct_pct: number
  value_type: string
  inferred_kind?: string | null
  examples: string[]
  avg_len: number
}

export interface Recommendation {
  source: LogSource
  format_confidence: number
  format_alternatives: [string, number][]
  profiles: FieldProfile[]
  estimated_bytes_per_event: number
  estimated_bytes_if_keep_all: number
  notes: string[]
  savings_pct: number
  field_advice: { field: string; suggested_name?: string; meaning: string; recommendation: string }[]
  ai_available: boolean
}

export interface PreviewResponse {
  events: BastionEvent[]
  dropped_by_sampling: number
  unmapped_fields: string[]
  warnings: string[]
  config_warnings: string[]
}

export interface Rule {
  id: string
  version: number
  tenant: string
  name: string
  description: string
  severity: Severity
  trigger: Record<string, unknown>
  condition: Record<string, unknown>
  techniques: Technique[]
  pir_ids: string[]
  throttle: { window_ms: number; key?: unknown }
  evidence_cap: number
  enabled: boolean
  author: string
  source: string
  triage_guidance: string
  playbook_id?: string | null
  warnings: string[]
  unknown_fields: string[]
}

export interface ActionSpec {
  id: string
  name: string
  description: string
  category: string
  blast_radius: string
  reverses_with?: string | null
  params: { name: string; description: string; required: boolean; suggested_binding: string }[]
  requires_integration: string
  typical_duration_ms: number
  requires_approval: boolean
}

export interface PlaybookNode {
  id: string
  label: string
  kind: string
  position: { x: number; y: number }
  notes?: string
  action_id?: string
  params?: Record<string, unknown>
  continue_on_error?: boolean
  question?: string
  branches?: string[]
  expression?: string
  prompt?: string
  approvers?: string[]
  timeout_ms?: number
  on_timeout?: string
  duration_ms?: number
  outcome?: string
}

export interface PlaybookEdge {
  from: string
  to: string
  branch?: string | null
}

export interface Playbook {
  id: string
  tenant: string
  name: string
  description: string
  version: number
  trigger: {
    min_severity?: Severity | null
    techniques: string[]
    tactics: string[]
    rule_ids: string[]
    automatic: boolean
  }
  nodes: PlaybookNode[]
  edges: PlaybookEdge[]
  enabled: boolean
  author: string
  errors: string[]
  advisories: string[]
  max_blast_radius: string
  required_integrations: string[]
}

export interface StepRecord {
  at: number
  node_id: string
  node_label: string
  detail: string
  outcome: string
}

export interface PlaybookRun {
  id: string
  tenant: string
  playbook_id: string
  playbook_version: number
  incident_id: string
  channel_id: string
  state: string
  steps: StepRecord[]
  /** Node ids whose action was never really performed, because no integration was configured. */
  simulated_steps?: string[]
  awaiting: unknown
  outcome: string
  started_at: number
  finished_at?: number
}

export interface Feed {
  id: string
  name: string
  description: string
  provider: string
  kind: string
  docs_url: string
  requires_key: boolean
  fields: { name: string; label: string; secret: boolean; help: string }[]
  scaffold: boolean
  supplies: string[]
  status: string
  configured: boolean
  indicator_count: number
  warnings: string[]
  /** Absent means never polled by this process, which is not the same as polled and empty. */
  last_fetch?: FeedFetch
}

export interface FeedFetch {
  feed_id: string
  seen: number
  accepted: number
  skipped: number
  error: string
  at: number
}

export interface Suppression {
  kind: string
  value: string
  reason: string
  created_by: string
  created_at: number
  expires_at: number
  /** Matches this has prevented. The number that says whether the decision still makes sense. */
  suppressed_count: number
  days_left: number
  expiring_soon: boolean
}

export interface SigmaImport {
  rule: Rule
  warnings: string[]
  /** Fields the rule reads that no configured source produces. Usually non-empty. */
  unknown_fields: string[]
  saved: boolean
}

export interface Backtest {
  rule_id: string
  events_considered: number
  /** Events whose condition matched, before the trigger decided anything. */
  matched: number
  signals: number
  samples: Signal[]
  by_day: [string, number][]
  window_from?: number
  window_to?: number
  /** What this result does not cover. Always shown. */
  notes: string[]
}

export interface QueryFilter {
  text?: string
  severity?: string | null
  source?: string
  class?: string
  limit?: number
}

export interface SavedQuery {
  id: string
  tenant: string
  name: string
  description: string
  target: 'events' | 'signals' | 'incidents'
  filter: QueryFilter
  created_by: string
  created_at: number
  updated_at: number
}

export interface QueryResult {
  target: string
  rows: Record<string, unknown>[]
  matched: number
  truncated: boolean
  /** Set when the window searched is not the whole history. */
  window_note?: string
}

/** Where a card sits on the 12-column grid. Absent means "wherever the list puts it". */
export interface Placement {
  x: number
  y: number
  w: number
  h: number
}

export const GRID_COLUMNS = 12

export type DashboardCard = { id: string; title?: string; placement?: Placement } & (
  | { kind: 'saved_query'; query_id: string; rows?: number }
  | { kind: 'volume'; days?: number }
  | { kind: 'coverage' }
  | { kind: 'incidents_by_severity' }
  | { kind: 'note'; text: string }
)

export interface Dashboard {
  id: string
  tenant: string
  name: string
  description: string
  cards: DashboardCard[]
  is_default: boolean
  created_by: string
  created_at: number
  updated_at: number
}

export interface RollbackStep {
  from_node: string
  undoes: string
  action_id: string
  params: Record<string, string[]>
  why: string
  uncertain: boolean
}

export interface RollbackSkipped {
  node_id: string
  action_id: string
  reason: string
  /** The estate was changed and this plan cannot undo it — somebody has to, by hand. */
  still_applied: boolean
}

export interface RollbackPlan {
  run_id: string
  incident_id: string
  steps: RollbackStep[]
  skipped: RollbackSkipped[]
}

export interface Indicator {
  value: string
  kind: string
  category: string
  confidence: string
  source: string
  first_seen: number
  last_seen: number
  valid_until: number
  techniques: Technique[]
  actors: string[]
  description: string
  references: string[]
  pir_ids: string[]
  effective_confidence: number
  expired: boolean
}

export interface Pir {
  id: string
  title: string
  question: string
  priority: 'low' | 'medium' | 'high' | 'critical'
  techniques: Technique[]
  actors: string[]
  assets: string[]
  feeds: string[]
  rule_ids: string[]
  created_at: number
  created_by: string
  active: boolean
  review_at?: number
  coverage_pct: number
  uncovered: Technique[]
}

/** A rule the model proposed, alongside the deterministic verdict on it. Nothing here is saved. */
export interface RuleDraftResult {
  /** The proposal as a real rule, ready to edit and save. Null when the platform refused it. */
  rule: Rule | null
  draft: {
    name: string
    description: string
    severity: string
    techniques: string[]
    triage_guidance: string
    referenced_fields: string[]
    false_positive_notes: string
  } | null
  /** Why no rule is offered. Empty when one is. */
  refused: string
  /** What the validator said. Empty means it would be accepted unchanged. */
  validation: string[]
  /** Technique ids the model named that this requirement does not cover. */
  unmatched_techniques: string[]
  ai_available: boolean
}

/** One hour of the warm tier: how much of one shape of traffic arrived. */
export interface RollupBucket {
  bucket: number
  source: string
  class: string
  severity: string
  event_count: number
  byte_estimate: number
}

export interface VolumeResponse {
  buckets: RollupBucket[]
  total_events: number
  total_bytes: number
  days: number
  /** Non-empty when there is no warm tier to read, so an empty chart is never ambiguous. */
  unavailable_reason: string
}

export interface CoverageGap {
  pir_id: string
  pir_title: string
  priority: string
  uncovered: Technique[]
  coverage_pct: number
  has_feed: boolean
}

export interface Coverage {
  weighted_coverage_pct: number
  covered_techniques: string[]
  gaps: CoverageGap[]
  missing_feeds: string[]
  due_for_review: string[]
  by_tactic: Record<
    string,
    { id: string; rule_count: number; techniques: { id: string; name: string }[]; late_stage: boolean }
  >
}

export interface SandboxReport {
  sha256: string
  filename: string
  analyzed_at: number
  static_analysis: {
    sha256: string
    size_bytes: number
    format: string
    entropy: number
    strings: string[]
    observables: Observable[]
    findings: { id: string; title: string; detail: string; severity: Severity; techniques: string[] }[]
    imports: string[]
  }
  dynamic?: unknown
  dynamic_unavailable_reason: string
  verdict: string
  summary: string
}
